import { FieldNames } from '../../models/fields/names';
import {
  hasFSCashAccount,
  hasFSCreditCard,
  hasFSLifeInsurance,
  hasFSLoan,
  hasFSMortgage,
  hasFSNote,
  hasFSPersonalProperty,
  hasFSRealEstate,
  hasFSRetirementAccount,
  hasFSSpouse,
  hasFSStock,
  hasFSTax,
} from '../../models/fields/conditionals';
import {
  BorrowerType,
  FinancialStatement,
  FinancialStatementBorrower,
  FinancialStatementCashAccount,
  FinancialStatementCreditCard,
  FinancialStatementLifeInsurance,
  FinancialStatementLoansPayable,
  FinancialStatementMortgages,
  FinancialStatementNotesAndContracts,
  FinancialStatementPersonalProperty,
  FinancialStatementRealEstate,
  FinancialStatementRetirementAccount,
  FinancialStatementStock,
  FinancialStatementTaxesPayable,
} from '@lenderful/domain';

export const mapFinancialStatement = (formData) => {
  const cashAccounts       = mapCashAccounts(formData);
  const creditCards        = mapCreditCards(formData);
  const lifeInsurance      = mapLifeInsurance(formData);
  const loans              = mapLoans(formData);
  const mortgages          = mapMortgages(formData);
  const notesAndContracts  = mapNotesAndContracts(formData);
  const personalProperties = mapPersonalProperty(formData);
  const realEstate         = mapRealEstate(formData);
  const retirementAccounts = mapRetirementAccounts(formData);
  const stocks             = mapStocks(formData);
  const taxes              = mapTaxes(formData);

  const borrower = mapFinancialStatementBorrower(formData, BorrowerType.PRIMARY);
  const spouse = hasFSSpouse(formData) ? mapFinancialStatementBorrower(formData, BorrowerType.COBORROWER) : undefined;
  const borrowers = spouse ? [borrower, spouse] : [borrower];

  const financialStatementRequest: FinancialStatement = {
    borrowers,
    street          : formData[FieldNames.livingPropertyStreet],
    street2         : formData[FieldNames.livingPropertyStreet2],
    city            : formData[FieldNames.livingPropertyCity],
    state           : formData[FieldNames.livingPropertyState],
    zipCode         : formData[FieldNames.livingPropertyZip],
    businessName    : formData[FieldNames.commercialBusinessName],
    businessStreet  : formData[FieldNames.commercialStreet],
    businessStreet2 : formData[FieldNames.commercialStreet2],
    businessCity    : formData[FieldNames.commercialCity],
    businessState   : formData[FieldNames.commercialState],
    businessZipCode : formData[FieldNames.commercialZip],
    businessPhone   : formData[FieldNames.commercialPhone],
    businessTitle   : formData[FieldNames.commercialBusinessTitle],
    cashAccounts,
    creditCards,
    lifeInsurance,
    loans,
    mortgages,
    notesAndContracts,
    personalProperties,
    realEstate,
    retirementAccounts,
    stocks,
    taxes,
  };
  return financialStatementRequest;
};

const mapFinancialStatementBorrower = (formData: any, borrowerType: BorrowerType): FinancialStatementBorrower => ({
  firstName: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.firstName] : formData[FieldNames.financialStatementSpouseFirstName],
  middleName: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.middleName] : formData[FieldNames.financialStatementSpouseMiddleName],
  lastName: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.lastName] : formData[FieldNames.financialStatementSpouseLastName],
  suffixName: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.suffixName] : formData[FieldNames.financialStatementSpouseSuffixName],
  dateOfBirth: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.dateOfBirth] : formData[FieldNames.financialStatementSpouseDateOfBirth],
  email: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.email] : formData[FieldNames.financialStatementSpouseEmail],
  phone: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.phone] : formData[FieldNames.financialStatementSpousePhone],
  borrowerType,
  hasBankrupt: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.decBorrower][FieldNames.hasBankrupt] : formData[FieldNames.decCoBorrower][FieldNames.hasBankrupt],
  hasJudgements: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.decBorrower][FieldNames.hasJudgements] : formData[FieldNames.decCoBorrower][FieldNames.hasJudgements],
  hasForeclosed: borrowerType === BorrowerType.PRIMARY ? formData[FieldNames.decBorrower][FieldNames.hasForeclosed] : formData[FieldNames.decCoBorrower][FieldNames.hasForeclosed],
});

const mapCashAccounts = (formData: any): FinancialStatementCashAccount[] => {
  const cashAccounts: FinancialStatementCashAccount[] = [];
  if(hasFSCashAccount(formData)) {
    formData[FieldNames.financialStatementCashAccount].forEach(element => {
      cashAccounts.push({
        accountType          : element[FieldNames.financialStatementAccountType],
        financialInstitution : element[FieldNames.financialStatementFinancialInstitution],
        jointAccount         : element[FieldNames.financialStatementJointAccount],
        currentBalance       : element[FieldNames.financialStatementCurrentBalance],
      });
    });
  }
  return cashAccounts;
};

const mapCreditCards = (formData: any): FinancialStatementCreditCard[] => {
  const creditCards: FinancialStatementCreditCard[] = [];
  if(hasFSCreditCard(formData)) {
    formData[FieldNames.financialStatementCreditCards].forEach(element => {
      creditCards.push({
        creditor       : element[FieldNames.financialStatementCreditor],
        currentBalance : element[FieldNames.financialStatementCurrentBalance],
        monthlyPayment : element[FieldNames.financialStatementMonthlyPayment],
        jointAccount   : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return creditCards;
};

const mapLifeInsurance = (formData: any): FinancialStatementLifeInsurance[] => {
  const lifeInsurance: FinancialStatementLifeInsurance[] = [];
  if(hasFSLifeInsurance(formData)) {
    formData[FieldNames.financialStatementLifeInsurance].forEach(element => {
      lifeInsurance.push({
        company      : element[FieldNames.financialStatementCompany],
        beneficiary  : element[FieldNames.financialStatementBeneficiary],
        faceValue    : element[FieldNames.financialStatementFaceValue],
        cashValue    : element[FieldNames.financialStatementCashValue],
        jointAccount : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return lifeInsurance;
};

const mapLoans = (formData: any): FinancialStatementLoansPayable[] => {
  const loans: FinancialStatementLoansPayable[] = [];
  if(hasFSLoan(formData)) {
    formData[FieldNames.financialStatementLoans].forEach(element => {
      loans.push({
        creditor       : element[FieldNames.financialStatementCreditor],
        currentBalance : element[FieldNames.financialStatementCurrentBalance],
        description    : element[FieldNames.financialStatementDescription],
        securedBy      : element[FieldNames.financialStatementSecuredBy],
        maturityDate   : element[FieldNames.financialStatementMaturityDate],
        monthlyPayment : element[FieldNames.financialStatementMonthlyPayment],
        jointAccount   : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return loans;
};

const mapMortgages = (formData: any): FinancialStatementMortgages[] => {
  const mortgages: FinancialStatementMortgages[] = [];
  if(hasFSMortgage(formData)) {
    formData[FieldNames.financialStatementMortgages].forEach(element => {
      mortgages.push({
        creditor       : element[FieldNames.financialStatementCreditor],
        currentBalance : element[FieldNames.financialStatementCurrentBalance],
        location       : element[FieldNames.financialStatementLocation],
        maturityDate   : element[FieldNames.financialStatementMaturityDate],
        monthlyPayment : element[FieldNames.financialStatementMonthlyPayment],
        jointAccount   : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return mortgages;
};

const mapNotesAndContracts = (formData: any): FinancialStatementNotesAndContracts[] => {
  const notesAndContracts: FinancialStatementNotesAndContracts[] = [];
  if(hasFSNote(formData)) {
    formData[FieldNames.financialStatementNotes].forEach(element => {
      notesAndContracts.push({
        balanceOwed    : element[FieldNames.financialStatementBalanceOwed],
        maturityDate   : element[FieldNames.financialStatementMaturityDate],
        monthlyPayment : element[FieldNames.financialStatementMonthlyPayment],
        purpose        : element[FieldNames.financialStatementNotePurpose],
        owingParty     : element[FieldNames.financialStatementOwingParty],
        jointAccount   : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return notesAndContracts;
};

const mapPersonalProperty = (formData: any): FinancialStatementPersonalProperty[] => {
  const personalProperties: FinancialStatementPersonalProperty[] = [];
  if(hasFSPersonalProperty(formData)) {
    formData[FieldNames.financialStatementPersonalProperties].forEach(element => {
      personalProperties.push({
        description  : element[FieldNames.financialStatementDescription],
        marketValue  : element[FieldNames.financialStatementMarketValue],
        propertyType : element[FieldNames.financialStatementPersonalType],
        jointAccount : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return personalProperties;
};

const mapRealEstate = (formData: any): FinancialStatementRealEstate[] => {
  const realEstate: FinancialStatementRealEstate[] = [];
  if(hasFSRealEstate(formData)) {
    formData[FieldNames.financialStatementRealEstate].forEach(element => {
      realEstate.push({
        description     : element[FieldNames.financialStatementDescription],
        location        : element[FieldNames.financialStatementLocation],
        marketValue     : element[FieldNames.financialStatementMarketValue],
        purchaseDate    : element[FieldNames.financialStatementPurchaseDate],
        jointAccount    : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return realEstate;
};

const mapRetirementAccounts = (formData: any): FinancialStatementRetirementAccount[] => {
  const retirementAccounts: FinancialStatementRetirementAccount[] = [];
  if(hasFSRetirementAccount(formData)) {
    formData[FieldNames.financialStatementRetirementAccounts].forEach(element => {
      retirementAccounts.push({
        accountType          : element[FieldNames.financialStatementRetirementType],
        financialInstitution : element[FieldNames.financialStatementFinancialInstitution],
        marketValue          : element[FieldNames.financialStatementMarketValue],
        jointAccount         : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return retirementAccounts;
};

const mapStocks = (formData: any): FinancialStatementStock[] => {
  const stocks: FinancialStatementStock[] = [];
  if(hasFSStock(formData)) {
    formData[FieldNames.financialStatementStocks].forEach(element => {
      stocks.push({
        marketValue    : element[FieldNames.financialStatementMarketValue],
        numberOfShares : element[FieldNames.financialStatementNumOfShares],
        securityName   : element[FieldNames.financialStatementSecurityName],
        jointAccount   : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return stocks;
};

const mapTaxes = (formData: any): FinancialStatementTaxesPayable[] => {
  const taxes: FinancialStatementTaxesPayable[] = [];
  if(hasFSTax(formData)) {
    formData[FieldNames.financialStatementTaxes].forEach(element => {
      taxes.push({
        amount       : element[FieldNames.financialStatementAmountOwed],
        description  : element[FieldNames.financialStatementDescription],
        taxType      : element[FieldNames.financialStatementTaxType],
        jointAccount : element[FieldNames.financialStatementJointAccount],
      });
    });
  }
  return taxes;
};
